import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/ui/Icon'
import { ThemeToggle } from 'components/chat-page/ThemeToggle'
import { settingsSelectors } from 'store/settings'
import { useModalActions } from 'providers/ModalProvider'
import { useIsAuthModeExternal, useIsAuthModeNoLogin } from 'hooks/useAuthMode'
import { useThemePreference } from 'hooks/useThemePreference'
import { ThemeEnum } from 'types/setting'
import { useLogout } from 'hooks/useLogout'
import { getUserDisplayName, getUserInitial } from 'utils/user'

export const ChatPageSidebarFooter = () => {
  const { t } = useTranslation()
  const currentUser = useSelector(settingsSelectors.userDetails)
  const isLogged = useSelector(settingsSelectors.isLogged)
  const isExternal = useIsAuthModeExternal()
  const isNoLogin = useIsAuthModeNoLogin()
  const isAutoTheme = useThemePreference() === ThemeEnum.Auto
  const { showLogin } = useModalActions()
  const handleLogout = useLogout()

  const showLoginButton = !isLogged && !isExternal && !isNoLogin

  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-p-3" style={{ borderTop: '1px solid var(--page-border)' }}>
      {isAutoTheme && (
        <div className="tw-flex tw-items-center tw-justify-between tw-px-2.5">
          <span className="tw-text-xs tw-font-medium" style={{ color: 'var(--text-tertiary)' }}>
            {t('theme')}
          </span>
          <ThemeToggle />
        </div>
      )}

      {isLogged && currentUser ? (
        <div
          className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-px-2.5 tw-py-2.5"
          style={{ background: 'var(--page-surface-hover)' }}
        >
          <div
            className="tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-sm tw-font-semibold tw-text-white"
            style={{
              background: 'linear-gradient(167deg, var(--primary-gradient-start), var(--primary-gradient-end))',
            }}
          >
            {getUserInitial(currentUser)}
          </div>
          <div className="tw-min-w-0 tw-flex-1">
            <div className="tw-truncate tw-text-sm tw-font-medium" style={{ color: 'var(--text-primary)' }}>
              {getUserDisplayName(currentUser)}
            </div>
            {currentUser.email && (
              <div className="tw-truncate tw-text-xs" style={{ color: 'var(--text-tertiary)' }}>
                {currentUser.email}
              </div>
            )}
          </div>
          {!isExternal && (
            <button
              type="button"
              onClick={handleLogout}
              title={t('logout')}
              aria-label={t('logout')}
              className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-transition-colors hover:tw-bg-[var(--input-bg)]"
            >
              <Icon
                icon={['far', 'sign-out-alt']}
                className="tw-h-4 tw-w-4"
                style={{ color: 'var(--text-tertiary)' }}
              />
            </button>
          )}
        </div>
      ) : showLoginButton ? (
        <button
          type="button"
          onClick={() => showLogin()}
          className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-px-2.5 tw-py-2.5 tw-text-left tw-transition-all tw-duration-200 hover:tw-scale-[1.01] active:tw-scale-[0.99]"
          style={{ background: 'var(--page-surface-hover)' }}
        >
          <div
            className="tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full"
            style={{
              background: 'linear-gradient(167deg, var(--primary-gradient-start), var(--primary-gradient-end))',
            }}
          >
            <Icon icon={['far', 'sign-in-alt']} className="tw-h-4 tw-w-4" style={{ color: 'white' }} />
          </div>
          <span className="tw-flex-1 tw-truncate tw-text-sm tw-font-medium" style={{ color: 'var(--text-primary)' }}>
            {t('login')}
          </span>
          <Icon
            icon={['far', 'chevron-right']}
            className="tw-h-3.5 tw-w-3.5"
            style={{ color: 'var(--text-tertiary)' }}
          />
        </button>
      ) : null}
    </div>
  )
}
